import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import TouchAppIcon from '@mui/icons-material/TouchApp';

/**
 * Code couleur du firmware V3 (carte MPB_RPI4B-V3.0, voyant RGB du bornier J18).
 *
 * Les teintes et les cadences reproduisent core/led_etats.py, seul endroit du
 * firmware où une couleur et un motif sont définis. Toute modification du
 * firmware doit être reportée ici.
 */

interface CodeV3 {
  etat: string;
  teinte: string;
  rgb: string;
  rgbAlt?: string;
  /** Durée du cycle complet, en millisecondes. */
  periode: number;
  /** Durée d'un éclat, en millisecondes. */
  eclat: number;
  /** Nombre d'éclats par cycle. Au-delà de 1, ils forment une salve. */
  eclats?: number;
  signal: string;
  actions: string[];
}

// Salve des défauts de périphérique : 3 éclats de 200 ms séparés de 200 ms,
// puis extinction jusqu'à la fin des 5 secondes.
const SALVE = { periode: 5000, eclat: 200, eclats: 3 };

const codes: CodeV3[] = [
  {
    etat: 'Fonctionnement normal',
    teinte: 'Vert',
    rgb: 'rgb(0,255,0)',
    periode: 5000, eclat: 200,
    signal: 'Un éclat bref toutes les 5 s',
    actions: ['Aucune action requise'],
  },
  {
    etat: 'Démarrage du boîtier',
    teinte: 'Cyan',
    rgb: 'rgb(0,255,255)',
    periode: 1000, eclat: 500,
    signal: '0,5 s allumé, 0,5 s éteint',
    actions: ["Attendre la fin de l'initialisation"],
  },
  {
    etat: 'Réseau WiFi : connexion en cours',
    teinte: 'Bleu',
    rgb: 'rgb(0,0,255)',
    periode: 1000, eclat: 500,
    signal: '0,5 s allumé, 0,5 s éteint',
    actions: ['Patienter', 'Vérifier la portée du réseau si cela dure'],
  },
  {
    etat: 'Réseau WiFi : défaut',
    teinte: 'Bleu',
    rgb: 'rgb(0,0,255)',
    ...SALVE,
    signal: '3 éclats toutes les 5 s',
    actions: [
      'Vérifier le SSID et le mot de passe dans device_config.json',
      'Vérifier la portée du réseau',
      "Redémarrer le point d'accès",
    ],
  },
  {
    etat: 'Broker MQTT : connexion en cours',
    teinte: 'Magenta',
    rgb: 'rgb(255,0,255)',
    periode: 1000, eclat: 500,
    signal: '0,5 s allumé, 0,5 s éteint',
    actions: ['Patienter'],
  },
  {
    etat: 'Broker MQTT : défaut',
    teinte: 'Magenta',
    rgb: 'rgb(255,0,255)',
    ...SALVE,
    signal: '3 éclats toutes les 5 s',
    actions: [
      "Vérifier l'adresse et le port du broker",
      'Vérifier les identifiants MQTT',
      'Vérifier que Mosquitto est actif sur le serveur',
    ],
  },
  {
    etat: 'Sonde en défaut',
    teinte: 'Orange',
    rgb: 'rgb(255,52,0)',
    ...SALVE,
    signal: '3 éclats toutes les 5 s',
    actions: [
      'Identifier la sonde sur la rangée de voyants RS485 (voir plus bas)',
      'Vérifier le câblage RS485 et les adresses Modbus',
      'Une seule sonde en défaut suffit à allumer ce voyant',
    ],
  },
  {
    etat: 'Défaut système',
    teinte: 'Rouge',
    rgb: 'rgb(255,0,0)',
    periode: 1500, eclat: 750,
    signal: '0,75 s allumé, 0,75 s éteint',
    actions: [
      'Redémarrer le boîtier',
      "Vérifier l'espace disque : df -h",
      'Consulter le journal : journalctl -u mpb_v3 -n 50',
    ],
  },
  {
    etat: 'Défaut non identifié',
    teinte: 'Rouge, puis bleu un battement sur deux',
    rgb: 'rgb(255,0,0)',
    rgbAlt: 'rgb(0,0,255)',
    periode: 1500, eclat: 750,
    signal: '0,75 s allumé, 0,75 s éteint, en changeant de couleur',
    actions: [
      'Consulter le journal : /var/lib/mpb_v3/mpb_v3.log',
      'Envoyer un diagnostic : appui bouton de 2 à 5 secondes',
      'Contacter le support technique',
    ],
  },
  {
    etat: 'Avertissement',
    teinte: 'Jaune',
    rgb: 'rgb(255,255,0)',
    periode: 5000, eclat: 200,
    signal: 'Un éclat bref toutes les 5 s, comme le fonctionnement normal',
    actions: [
      'Secteur 230 V absent, température du coffret supérieure à 55 °C,',
      'horloge temps réel perdue, ou stockage local proche de la saturation',
      'Le détail figure dans les diagnostics du boîtier',
    ],
  },
];


/** Pastille reproduisant la cadence exacte du firmware, salves comprises. */
const Pastille: React.FC<{ code: CodeV3 }> = ({ code }) => {
  const nom = `led-${code.etat.replace(/[^a-z0-9]/gi, '')}`;
  const eclats = code.eclats ?? 1;
  const alterne = Boolean(code.rgbAlt);
  // Un motif alterné change de couleur à chaque cycle : la période
  // d'animation double pour porter les deux couleurs.
  const duree = alterne ? code.periode * 2 : code.periode;
  const pas = code.eclat * 2; // éclat + silence de même durée
  const images: Record<string, { backgroundColor: string }> = {};
  const poser = (debutMs: number, couleur: string) => {
    for (let n = 0; n < eclats; n += 1) {
      const t0 = debutMs + n * pas;
      images[`${(t0 / duree) * 100}%`] = { backgroundColor: couleur };
      images[`${((t0 + code.eclat) / duree) * 100}%`] = { backgroundColor: 'transparent' };
    }
  };
  images['0%'] = { backgroundColor: 'transparent' };
  poser(0, code.rgb);
  if (alterne) poser(code.periode, code.rgbAlt as string);
  images['100%'] = { backgroundColor: 'transparent' };
  return (
    <Box
      sx={{
        width: 16,
        height: 16,
        borderRadius: '50%',
        border: '1px solid rgba(128,128,128,0.4)',
        flexShrink: 0,
        [`@keyframes ${nom}`]: images,
        animation: `${nom} ${duree}ms step-end infinite`,
      }}
    />
  );
};


const boutonV3 = [
  { duree: 'Moins de 2 secondes', retour: '—', action: 'Aucune action' },
  { duree: '2 à 5 secondes', retour: '—', action: 'Envoi d\'un diagnostic' },
  {
    duree: '5 à 10 secondes',
    retour: 'Vert fixe pendant l\'appui',
    action: 'Remise en service des sondes écartées, puis mesure',
  },
  {
    duree: '10 à 15 secondes',
    retour: 'Rouge clignotant lent',
    action: 'Coupure de tous les relais',
  },
  {
    duree: 'Plus de 15 secondes',
    retour: 'Rouge clignotant rapide',
    action: 'Redémarrage du boîtier',
  },
];

const LEDCodesV3: React.FC = () => {
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 2 }}>
        La couleur donne le domaine, la cadence la nature du signal.
        Un éclat toutes les 5 s : marche normale. Battement régulier : attente
        ou défaut du boîtier. Trois éclats toutes les 5 s : défaut d'un
        périphérique, la couleur dit lequel. Voyant fixe : le firmware ne
        tourne pas.
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Couleur</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>État</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Signal</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {codes.map((code) => (
              <TableRow key={code.etat}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Pastille code={code} />
                    <Typography variant="body2">{code.teinte}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">{code.etat}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{code.signal}</Typography>
                </TableCell>
                <TableCell>
                  <List dense disablePadding sx={{ '& .MuiListItem-root': { py: 0, px: 0 } }}>
                    {code.actions.map((action, i) => (
                      <ListItem key={i} disableGutters>
                        <ListItemText
                          primary={`• ${action}`}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" gutterBottom>Voyants de façade</Typography>
      <List dense sx={{ mb: 2 }}>
        <ListItem disableGutters>
          <ListItemText
            primary="Rangée RS485, six voyants (borniers 1 à 6)"
            secondary="Pendant un balayage, la rangée défile du bornier 1 au 6 puis reboucle, 600 ms chacun, un seul voyant à la fois. Hors balayage, rangée éteinte. La durée est la même pour tous : une sonde muette se lit sur le voyant RGB en orange. Un voyant jamais allumé : bornier vide ou sonde non déclarée."
          />
        </ListItem>
        <ListItem disableGutters>
          <ListItemText
            primary="Voyants relais 1 et 2"
            secondary="Allumés quand le relais correspondant est actif."
          />
        </ListItem>
        <ListItem disableGutters>
          <ListItemText
            primary="Voyants DI 1 et DI 2"
            secondary="Allumés quand un débit non nul est mesuré sur la voie correspondante."
          />
        </ListItem>
        <ListItem disableGutters>
          <ListItemText
            primary="Voyant secteur"
            secondary="Câblé sur l'alimentation 230 V, indépendant du firmware. Il reste allumé lorsque le service est arrêté."
          />
        </ListItem>
      </List>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Test des voyants à la mise en service : <code>python3 /opt/mpb_v3/tools/test_leds.py</code>.
      </Typography>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <TouchAppIcon color="primary" />
        <Typography variant="h6">Bouton utilisateur</Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Durée d'appui</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Retour visuel</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {boutonV3.map((b) => (
              <TableRow key={b.duree}>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">{b.duree}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{b.retour}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{b.action}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default LEDCodesV3;
